import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Feather, Send, ArrowRight, Star, BookOpen } from "lucide-react";
import HeroIllustration from "@/components/HeroIllustration";
import KidsIllustration from "@/components/KidsIllustration";
import NextBookPopup from "@/components/NextBookPopup";
import FavoriteButton from "@/components/FavoriteButton";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    api.get("/books").then(({ data }) => {
      setFeatured(data.filter((b) => b.featured).slice(0, 3));
    }).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("You're on the list. A gentle note is on its way. 💌");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="home-page">
      <NextBookPopup />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <span className="font-caveat text-3xl block" style={{ color: "#FF6B9D" }}>
              once upon a small adventure…
            </span>
            <h1
              className="font-fredoka text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-2"
              style={{ color: "#2D3055" }}
              data-testid="home-hero-title"
            >
              Storybooks full of <span style={{ color: "#FF6B9D" }}>wonder</span>, <span style={{ color: "#6EC4E8" }}>discovery</span>, and every child&apos;s <span style={{ color: "#B49FFF" }}>bright</span> heart.
            </h1>
            <p className="mt-6 text-lg max-w-xl leading-relaxed" style={{ color: "#4A4E6D" }}>
              I&apos;m M.K. Bunch — a Mom of four writing whimsical picture books from Washington, D.C.
              My stories send little readers on big adventures, reminding them that
              having fun is serious business — and they are, without question, wonderfully special.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/books" className="btn-terracotta" data-testid="home-cta-books">
                Explore the Books <ArrowRight size={18} />
              </Link>
              <Link to="/about" className="btn-ghost-warm" data-testid="home-cta-about">
                Meet the Author
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1">
                {[0,1,2,3,4].map((i) => <Star key={i} size={16} fill="#FFD93D" strokeWidth={0} />)}
              </div>
              <span className="text-sm font-nunito" style={{ color: "#6B6E88" }}>Loved by thousands of small readers &amp; their grown-ups</span>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="float-slow">
              <HeroIllustration className="w-full h-auto drop-shadow-2xl" />
            </div>
            <div
              className="absolute -bottom-4 -left-2 sm:-left-6 rounded-3xl px-5 py-3 hidden sm:block rotate-[-4deg]"
              style={{ background: "#FFD93D", boxShadow: "0 10px 30px rgba(255,217,61,0.55)" }}
              data-testid="home-hero-badge"
            >
              <p className="font-caveat text-2xl leading-tight" style={{ color: "#2D3055" }}>
                &quot;Read it again!&quot;
              </p>
              <p className="text-xs font-nunito" style={{ color: "#4A4E6D" }}>— every child, everywhere</p>
            </div>
          </div>
        </div>

        {/* Colorful confetti band */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-4">
          <div className="flex items-center justify-around flex-wrap gap-4 py-4">
            {[
              { c: "#FF6B9D", label: "adventure" },
              { c: "#6EC4E8", label: "discovery" },
              { c: "#4ECDC4", label: "kindness" },
              { c: "#B49FFF", label: "imagination" },
              { c: "#FFD93D", label: "joy" },
              { c: "#FF8C42", label: "courage" },
            ].map((tag, i) => (
              <div key={tag.label} className="flex items-center gap-2 font-caveat text-xl" style={{ color: "#2D3055", opacity: 0.85 }}>
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: tag.c }} />
                {tag.label}
                {i < 5 && <span className="text-[#B49FFF] hidden sm:inline">✦</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BOOKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="home-featured-section">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="font-caveat text-2xl" style={{ color: "#6EC4E8" }}>from the shelf</span>
            <h2 className="font-fredoka text-4xl sm:text-5xl" style={{ color: "#2D3055" }}>
              Featured stories
            </h2>
          </div>
          <Link to="/books" className="btn-ghost-warm" data-testid="home-see-all-books">
            See all books <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {featured.length === 0 && (
            <p className="col-span-3 font-nunito" style={{ color: "#7E82A1" }}>
              New stories being tucked in soon…
            </p>
          )}
          {featured.map((b, idx) => (
            <Link
              key={b.id}
              to={`/books/${b.id}`}
              className="warm-card p-5 fade-up book-tilt relative"
              style={{ animationDelay: `${idx * 120}ms` }}
              data-testid={`home-book-${b.id}`}
            >
              <div className="absolute top-3 left-3 z-10">
                <FavoriteButton bookId={b.id} variant="chip" />
              </div>
              <div
                className="rounded-2xl overflow-hidden mb-4 aspect-[3/4] flex items-center justify-center"
                style={{ background: "linear-gradient(160deg,#FFF3F8 0%,#FFFFFF 60%,#F0EBFF 100%)" }}
              >
                <img src={b.cover_image_url} alt={b.title} className="w-full h-full object-contain" />
              </div>
              <p className="font-caveat text-lg" style={{ color: "#FF6B9D" }}>{b.age_range}</p>
              <h3 className="font-fredoka text-2xl mt-1" style={{ color: "#2D3055" }}>{b.title}</h3>
              <p className="mt-2 text-sm line-clamp-3" style={{ color: "#4A4E6D" }}>{b.description}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* AUTHOR NOTE with illustrated kids */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div
          className="p-8 lg:p-14 grid lg:grid-cols-12 gap-10 items-center rounded-[2rem] relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(180,159,255,0.25) 0%, rgba(110,196,232,0.25) 50%, rgba(255,107,157,0.20) 100%)",
            border: "1px solid rgba(180,159,255,0.35)",
          }}
        >
          <div className="lg:col-span-5">
            <KidsIllustration className="w-full h-auto rounded-3xl float-slow" />
          </div>
          <div className="lg:col-span-7">
            <Feather size={28} style={{ color: "#6EC4E8" }} />
            <h2 className="font-fredoka text-4xl sm:text-5xl mt-4" style={{ color: "#2D3055" }}>
              For every child. In every corner of the world.
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: "#4A4E6D" }}>
              I write for the child who twirls in the kitchen. For the one who wonders about the moon.
              For the one who talks to snails, and the one who cries at the beauty of leaves. My stories
              are small love notes — full of adventure, discovery, and reminders that being yourself
              is the very best magic you were born with.
            </p>
            <Link to="/about" className="btn-moss mt-6" data-testid="home-author-cta">
              Read my story <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div
          className="p-10 lg:p-14 text-center relative overflow-hidden rounded-[2rem]"
          style={{
            background: "linear-gradient(135deg, #FFD93D 0%, #FF8C42 40%, #FF6B9D 100%)",
            boxShadow: "0 25px 60px rgba(255,107,157,0.25)",
          }}
        >
          <div className="absolute top-6 right-6 opacity-70">
            <Sparkles size={40} style={{ color: "#FFFFFF" }} />
          </div>
          <div className="absolute bottom-6 left-6 opacity-70">
            <BookOpen size={38} style={{ color: "#FFFFFF" }} />
          </div>
          <span className="font-caveat text-3xl" style={{ color: "#FFFFFF" }}>a cozy little letter</span>
          <h2 className="font-fredoka text-4xl mt-2" style={{ color: "#FFFFFF" }}>
            Join the story circle
          </h2>
          <p className="mt-4 max-w-lg mx-auto" style={{ color: "rgba(255,255,255,0.92)" }}>
            Gentle notes on new books, behind-the-page peeks, and the occasional tiny wonder.
            No noise. Just warmth. Unsubscribe whenever your heart tells you to.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="home-newsletter-form">
            <input
              type="email"
              required
              placeholder="your@lovelyemail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 rounded-full px-5 py-3 font-nunito focus:outline-none focus:ring-2"
              style={{
                background: "#fff",
                border: "1.5px solid rgba(255,255,255,0.6)",
                color: "#2D3055",
              }}
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={subscribing}
              className="rounded-full px-6 py-3 font-fredoka font-bold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5"
              style={{ background: "#2D3055", color: "#fff" }}
              data-testid="newsletter-submit-btn"
            >
              <Send size={16} /> {subscribing ? "Sending…" : "Subscribe"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
